import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import {
  RegFieldsEnum,
  RegistrationQuestion,
  RegistrationStep,
  RegistrationStepType,
} from '@/types/registration.types';
import Container from '@ui/container/container';
import SwitchV2 from '@ui/switch-v2/switch-v2';
import Text from '@ui/text/text/text';
import useComputedRegistrationSettings from '../../hooks/use-computed-registration-settings';
import useLoadRegistrationQuestions from '@/connection/hooks/use-load-registration-questions';
import { getRegistrationQuestions } from '@/store/slices/channel/selectors';
import { getWorkingProject, selectWorkingProjectRegistrationQuestions } from '@/store/slices/project/selectors';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import toastTrigger from '@ui/toast/toast-trigger';

export default function InPersonAttendeeModeToggle() {
  useLoadRegistrationQuestions();
  const dispatch = useAppDispatch();

  const workingProject = useAppSelector(getWorkingProject);
  const registrationQuestions = useAppSelector(selectWorkingProjectRegistrationQuestions);
  const { registrationSettings, registrationSteps, singleSignOnEnabled } = useComputedRegistrationSettings();
  const channelRegistrationQuestions = useAppSelector(getRegistrationQuestions);
  const requiredRegistrationQuestions = workingProject?.required_registration_questions;
  const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);

  const inPersonAttendeeModeIsOn = registrationSettings?.adminInPersonAttendeeModeEnabled;

  const handleToggle = (value: boolean, on: boolean) => {
    if (!registrationSettings || !registrationQuestions) return;

    const question = channelRegistrationQuestions.find(
      (question: RegistrationQuestion) => question.registration_question === RegFieldsEnum.attend_in_person
    );

    if (!question) {
      toastTrigger(
        <div className="S1">
          <div>There was an error toggling in-person attendee mode</div>
          <div>Please refresh and try again later</div>
        </div>,
        {
          type: 'error',
          autoClose: 10000,
        }
      );
      return;
    }

    let newSteps: RegistrationStep[] = [...registrationSteps];
    let newRequiredQuestions: number[] = [...(requiredRegistrationQuestions || [])];
    let newQuestions: RegistrationQuestion[] = [...registrationQuestions];
    // If the admin turns on in person attendee mode, add that question in the appropriate places. The default behavior is off.
    if (on && question?.registration_question) {
      newSteps = newSteps.map((step: RegistrationStep) => {
        if (step.type === RegistrationStepType.general) {
          return {
            ...step,
            questions: [...(step.questions || []), question],
          };
        }
        return step;
      });
      newRequiredQuestions = [...newRequiredQuestions, question?.registration_question];
      newQuestions = [...newQuestions, question];
    }
    // If the admin turns off in person attendee mode, remove that question from the appropriate places
    if (!on) {
      newRequiredQuestions = newRequiredQuestions?.filter(
        (requiredQuestion: number) => requiredQuestion !== question?.registration_question
      );
      newQuestions = newQuestions?.filter(
        (registrationQuestion: RegistrationQuestion) =>
          registrationQuestion.registration_question !== question?.registration_question
      );
      newSteps = newSteps.map((step: RegistrationStep) => {
        if (step.type === RegistrationStepType.general) {
          return {
            ...step,
            questions: step.questions?.filter(
              (stepQuestion: RegistrationQuestion) =>
                stepQuestion.registration_question !== question?.registration_question
            ),
          };
        }
        return step;
      });
    }

    dispatch(
      saveWorkingProjectByFields({
        registration_steps: newSteps,
        registration_questions: newQuestions,
        required_registration_questions: newRequiredQuestions,
        registration_settings: { ...registrationSettings, adminInPersonAttendeeModeEnabled: on },
      })
    );
  };

  return (
    <Container design="GlobalCont1" borderRadius="12px" padding="16px" className="flex ai-center jc-space-between">
      <Text tag="h6" className="H6">
        In-person attendee mode
      </Text>
      <div>
        <SwitchV2
          loading={!registrationSettings || savingWorkingProject}
          value={!!inPersonAttendeeModeIsOn}
          on={!!inPersonAttendeeModeIsOn}
          disabled={singleSignOnEnabled}
          onClick={handleToggle}
        />
      </div>
    </Container>
  );
}
